import type { Draw, Particle } from './engine';

/**
 * Particle shapes, drawn by code in a unit box (radius ≈ 1; the engine scales by `size`, rotates and flips).
 * No images: paths are cheap, crisp at any devicePixelRatio and recoloured per game. Rules of the house:
 * no coin piles, no "$", no 777, no 福, no digit 4 — mahjong tiles carry dots only.
 */
const TAU = Math.PI * 2;

const paths = new Map<string, Path2D | null>();
/** Path2D from SVG path data, cached (null where Path2D is missing — jsdom). */
function P(d: string): Path2D | null {
  let p = paths.get(d);
  if (p === undefined) {
    p = typeof Path2D === 'undefined' ? null : new Path2D(d);
    paths.set(d, p);
  }
  return p;
}
function fill(g: CanvasRenderingContext2D, d: string, rule: CanvasFillRule = 'nonzero'): void {
  const p = P(d);
  if (p) g.fill(p, rule);
}
function stroke(g: CanvasRenderingContext2D, d: string): void {
  const p = P(d);
  if (p) g.stroke(p);
}
function circle(g: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.fill();
}

// ---- soft light sprite (radial gradient, one per colour) ------------------------------------------------

const sprites = new Map<string, CanvasImageSource>();
export function glowSprite(col: string): CanvasImageSource | null {
  const hit = sprites.get(col);
  if (hit) return hit;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const x = c.getContext('2d');
  if (!x || typeof x.createRadialGradient !== 'function') return null;
  const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  if (!gr || typeof gr.addColorStop !== 'function') return null;
  gr.addColorStop(0, col);
  gr.addColorStop(0.35, col + 'AA');
  gr.addColorStop(1, col + '00');
  x.fillStyle = gr;
  x.fillRect(0, 0, 64, 64);
  sprites.set(col, c);
  return c;
}

// ---- basics -------------------------------------------------------------------------------------------

/** Soft round light (use with add: true). Colours must be #RRGGBB. */
export const glow: Draw = (g, p) => {
  const s = glowSprite(p.col);
  if (s) g.drawImage(s, -1, -1, 2, 2);
};

export const dot: Draw = (g, p) => {
  g.fillStyle = p.col;
  circle(g, 0, 0, 1);
};

/** Paper confetti (flip it). */
export const paper: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.fillRect(-1, -0.62, 2, 1.24);
};

/** A streak along its motion (face: true): size = thickness. */
export const spark: Draw = (g, p) => {
  const v = Math.hypot(p.vx, p.vy);
  const len = Math.min(26, 2 + v * 0.035) / Math.max(0.5, p.size);
  g.strokeStyle = p.col;
  g.lineCap = 'round';
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(-len, 0);
  g.lineTo(0, 0);
  g.stroke();
};

/** A four-point twinkle. */
export const twinkle: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M0-1Q.14-.14 1 0Q.14 .14 0 1Q-.14 .14-1 0Q-.14-.14 0-1Z');
};

export const star: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M0-1L.24-.33L.95-.31L.38 .12L.59 .81L0 .4L-.59 .81L-.38 .12L-.95-.31L-.24-.33Z');
};

/** Pixel square, no rotation (Neon). */
export const pixel: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.fillRect(-1, -1, 2, 2);
};

export const diamond: Draw = (g, p) => {
  g.strokeStyle = p.col;
  g.lineWidth = 0.28;
  g.beginPath();
  g.moveTo(0, -1);
  g.lineTo(1, 0);
  g.lineTo(0, 1);
  g.lineTo(-1, 0);
  g.closePath();
  g.stroke();
};

/** Smoke / steam puff: grey glow drawn normally (not additive). */
export const puff: Draw = (g, p) => {
  const s = glowSprite(p.col);
  if (s) g.drawImage(s, -1, -1, 2, 2);
};

// ---- garden -------------------------------------------------------------------------------------------

export const petal: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M0-1C.7-.62 .62 .56 0 1C-.62 .56-.7-.62 0-1Z');
  g.fillStyle = p.col2;
  g.globalAlpha *= 0.55;
  fill(g, 'M0-.82C.28-.5 .24 .2 0 .5C-.24 .2-.28-.5 0-.82Z');
};

export const leaf: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M-1 0Q-.1-.72 1 0Q-.1 .72-1 0Z');
  g.strokeStyle = p.col2;
  g.lineWidth = 0.09;
  stroke(g, 'M-.86 0L.9 0M-.2 0L.25-.26M.15 0L.55-.2M-.2 0L.25 .26M.15 0L.55 .2');
};

/** Butterfly: wings beat (data = beat phase); fly it with face: true. */
export const butterfly: Draw = (g, p) => {
  const beat = 0.25 + 0.75 * Math.abs(Math.sin(p.t * 13 + p.ph));
  g.save();
  g.rotate(Math.PI / 2);
  for (const side of [-1, 1]) {
    g.save();
    g.scale(side * beat, 1);
    g.fillStyle = p.col;
    fill(g, 'M0-.15C.35-1 1.05-1 .95-.42C.9-.1 .4 0 0 0Z');
    g.fillStyle = p.col2;
    fill(g, 'M0 0C.4 0 .82 .18 .7 .62C.58 .95 .16 .6 0 .15Z');
    g.fillStyle = '#FFFFFF';
    g.globalAlpha *= 0.5;
    circle(g, 0.58, -0.5, 0.12);
    g.restore();
  }
  g.fillStyle = '#3B2A2A';
  g.fillRect(-0.06, -0.5, 0.12, 1.05);
  g.restore();
};

// ---- deep ---------------------------------------------------------------------------------------------

export const bubble: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.globalAlpha *= 0.18;
  circle(g, 0, 0, 1);
  g.globalAlpha /= 0.18;
  g.strokeStyle = p.col;
  g.lineWidth = 0.12;
  g.beginPath();
  g.arc(0, 0, 0.94, 0, TAU);
  g.stroke();
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.85;
  circle(g, -0.38, -0.4, 0.2);
};

/** Fish heading along +x (use face: true). */
export const fish: Draw = (g, p) => {
  const wag = Math.sin(p.t * 16 + p.ph) * 0.18;
  g.fillStyle = p.col;
  fill(g, 'M1 0C.6-.5-.2-.52-.62 0C-.2 .52 .6 .5 1 0Z');
  g.save();
  g.translate(-0.58, 0);
  g.rotate(wag);
  fill(g, 'M0 0L-.48-.42L-.38 0L-.48 .42Z');
  g.restore();
  g.fillStyle = p.col2;
  fill(g, 'M.2-.32C.05-.62-.2-.6-.25-.3Z');
  g.fillStyle = '#0B1424';
  circle(g, 0.62, -0.08, 0.07);
};

// ---- spaceport ----------------------------------------------------------------------------------------

/** Planet with a tilted ring. */
export const planet: Draw = (g, p) => {
  g.strokeStyle = p.col2;
  g.lineWidth = 0.1;
  g.beginPath();
  g.ellipse(0, 0, 1, 0.32, -0.35, Math.PI, TAU);
  g.stroke();
  g.fillStyle = p.col;
  circle(g, 0, 0, 0.55);
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.35;
  circle(g, -0.18, -0.2, 0.18);
  g.globalAlpha /= 0.35;
  g.beginPath();
  g.ellipse(0, 0, 1, 0.32, -0.35, 0, Math.PI);
  g.stroke();
};

export const ring: Draw = (g, p) => {
  g.strokeStyle = p.col;
  g.lineWidth = 0.14;
  g.beginPath();
  g.ellipse(0, 0, 1, 0.38, 0, 0, TAU);
  g.stroke();
};

// ---- sweet shop ---------------------------------------------------------------------------------------

export const sprinkle: Draw = (g, p) => {
  g.strokeStyle = p.col;
  g.lineCap = 'round';
  g.lineWidth = 0.62;
  g.beginPath();
  g.moveTo(-0.7, 0);
  g.lineTo(0.7, 0);
  g.stroke();
};

export const lollipop: Draw = (g, p) => {
  g.strokeStyle = '#F4EDE4';
  g.lineWidth = 0.14;
  g.beginPath();
  g.moveTo(0, 0.5);
  g.lineTo(0, 1.5);
  g.stroke();
  g.fillStyle = p.col;
  circle(g, 0, 0, 0.75);
  g.strokeStyle = p.col2;
  g.lineWidth = 0.13;
  stroke(g, 'M0 0A.15 .15 0 0 1 .3 0A.3 .3 0 0 1-.3 0A.45 .45 0 0 1 .6 0A.6 .6 0 0 1-.6 .05');
};

export const candy: Draw = (g, p) => {
  g.fillStyle = p.col2;
  fill(g, 'M-.5 0L-1-.42L-.92 0L-1 .42Z');
  fill(g, 'M.5 0L1-.42L.92 0L1 .42Z');
  g.fillStyle = p.col;
  g.beginPath();
  g.ellipse(0, 0, 0.58, 0.42, 0, 0, TAU);
  g.fill();
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.45;
  g.beginPath();
  g.ellipse(-0.18, -0.16, 0.2, 0.09, -0.3, 0, TAU);
  g.fill();
};

/** Glass shard of the jar. */
export const shard: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.globalAlpha *= 0.55;
  fill(g, 'M-.6-1L.7-.2L-.1 1Z');
  g.globalAlpha /= 0.55;
  g.strokeStyle = '#FFFFFF';
  g.lineWidth = 0.08;
  stroke(g, 'M-.6-1L.7-.2');
};

// ---- workshop -----------------------------------------------------------------------------------------

function gearPath(teeth: number): string {
  let d = '';
  for (let i = 0; i < teeth; i++) {
    const a0 = (i / teeth) * TAU;
    const w = TAU / teeth;
    const pts: [number, number][] = [
      [a0, 0.74],
      [a0 + w * 0.12, 1],
      [a0 + w * 0.42, 1],
      [a0 + w * 0.54, 0.74],
    ];
    for (const [j, [a, r]] of pts.entries()) {
      d += `${i === 0 && j === 0 ? 'M' : 'L'}${(Math.cos(a) * r).toFixed(3)} ${(Math.sin(a) * r).toFixed(3)}`;
    }
  }
  return d + 'ZM.32 0A.32 .32 0 1 0-.32 0A.32 .32 0 1 0 .32 0Z';
}
const GEAR = gearPath(10);

export const gear: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, GEAR, 'evenodd');
};

export const nut: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(
    g,
    'M1 0L.5 .87L-.5 .87L-1 0L-.5-.87L.5-.87ZM.42 0A.42 .42 0 1 0-.42 0A.42 .42 0 1 0 .42 0Z',
    'evenodd',
  );
  g.strokeStyle = p.col2;
  g.lineWidth = 0.1;
  stroke(g, 'M.5-.87L1 0L.5 .87');
};

// ---- water: carp, koi, pirates ------------------------------------------------------------------------

export const drop: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M0-1C.42-.4 .62 0 .62 .32A.62 .62 0 0 1-.62 .32C-.62 0-.42-.4 0-1Z');
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.6;
  circle(g, -0.2, 0.2, 0.14);
};

export const lotus: Draw = (g, p) => {
  g.fillStyle = p.col;
  fill(g, 'M0-1C.5-.42 .46 .55 0 1C-.46 .55-.5-.42 0-1Z');
  g.fillStyle = p.col2;
  g.globalAlpha *= 0.7;
  fill(g, 'M0-1C.2-.5 .18 .3 0 .95C-.18 .3-.2-.5 0-1Z');
};

/** Koi heading along +x (face: true): long body, spots, flowing tail. */
export const koi: Draw = (g, p) => {
  const wag = Math.sin(p.t * 9 + p.ph) * 0.25;
  g.fillStyle = p.col;
  g.save();
  g.translate(-0.62, 0);
  g.rotate(wag);
  g.globalAlpha *= 0.85;
  fill(g, 'M0 0C-.3-.1-.5-.5-.62-.36C-.5-.1-.5 .1-.62 .36C-.5 .5-.3 .1 0 0Z');
  g.restore();
  fill(g, 'M1 0C.7-.34-.2-.36-.7 0C-.2 .36 .7 .34 1 0Z');
  g.fillStyle = p.col2;
  circle(g, 0.3, -0.06, 0.13);
  circle(g, -0.15, 0.08, 0.1);
  g.globalAlpha *= 0.7;
  fill(g, 'M.25-.22C.1-.5-.1-.52-.12-.26Z');
  fill(g, 'M.25 .22C.1 .5-.1 .52-.12 .26Z');
};

/** Seagull: a flapping "m" (face it left/right by flip sign in data). */
export const gull: Draw = (g, p) => {
  const f = Math.sin(p.t * 9 + p.ph) * 0.45;
  g.strokeStyle = p.col;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.lineWidth = 0.16;
  g.beginPath();
  g.moveTo(-1, -f);
  g.quadraticCurveTo(-0.5, -0.5 - f * 0.4, 0, 0.05);
  g.quadraticCurveTo(0.5, -0.5 - f * 0.4, 1, -f);
  g.stroke();
};

// ---- red festive: firecrackers, hongbao, lucky lever ---------------------------------------------------

export const firecracker: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.fillRect(-0.36, -0.9, 0.72, 1.8);
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.22;
  g.fillRect(-0.24, -0.9, 0.14, 1.8);
  g.globalAlpha /= 0.22;
  g.fillStyle = p.col2;
  g.fillRect(-0.4, -0.98, 0.8, 0.22);
  g.fillRect(-0.4, 0.76, 0.8, 0.22);
  g.strokeStyle = p.col2;
  g.lineWidth = 0.1;
  stroke(g, 'M0-.98Q.2-1.2 .1-1.4');
};

/** 红纸 — a square of red paper with a darker fold. */
export const redPaper: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.fillRect(-0.8, -0.8, 1.6, 1.6);
  g.fillStyle = p.col2;
  g.globalAlpha *= 0.5;
  fill(g, 'M-.8 .8L.8-.8L.8 .8Z');
};

/** Red envelope: a gold disc seal on the flap (no characters). */
export const envelope: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.beginPath();
  g.roundRect?.(-0.68, -1, 1.36, 2, 0.14);
  if (!g.roundRect) g.rect(-0.68, -1, 1.36, 2);
  g.fill();
  g.fillStyle = '#000000';
  g.globalAlpha *= 0.16;
  fill(g, 'M-.68-.98L0-.28L.68-.98Z');
  g.globalAlpha /= 0.16;
  g.strokeStyle = p.col2;
  g.lineWidth = 0.07;
  stroke(g, 'M-.66-.96L0-.28L.66-.96');
  g.fillStyle = p.col2;
  circle(g, 0, -0.3, 0.22);
};

export const lantern: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.beginPath();
  g.ellipse(0, 0, 0.82, 0.68, 0, 0, TAU);
  g.fill();
  g.strokeStyle = p.col2;
  g.globalAlpha *= 0.55;
  g.lineWidth = 0.06;
  stroke(g, 'M0-.68Q-.5 0 0 .68M0-.68Q.5 0 0 .68');
  g.globalAlpha /= 0.55;
  g.fillStyle = p.col2;
  g.fillRect(-0.36, -0.82, 0.72, 0.2);
  g.fillRect(-0.36, 0.62, 0.72, 0.2);
  g.strokeStyle = p.col2;
  g.lineWidth = 0.08;
  stroke(g, 'M-.12 .82L-.12 1.15M0 .82L0 1.25M.12 .82L.12 1.15');
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.35;
  g.beginPath();
  g.ellipse(-0.32, -0.26, 0.2, 0.1, -0.5, 0, TAU);
  g.fill();
};

/** 元宝 gold ingot: boat-shaped base with a dome. */
export const ingot: Draw = (g, p) => {
  g.fillStyle = p.col2;
  fill(g, 'M-1-.2C-.8 .55 .8 .55 1-.2C.6 .05-.6 .05-1-.2Z');
  g.fillStyle = p.col;
  fill(g, 'M-.42 0C-.42-.62 .42-.62 .42 0Z');
  fill(g, 'M-1-.2C-.7-.04-.45-.02-.42 0L.42 0C.45-.02 .7-.04 1-.2C.8 .18-.8 .18-1-.2Z');
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.45;
  g.beginPath();
  g.ellipse(-0.14, -0.32, 0.12, 0.07, -0.4, 0, TAU);
  g.fill();
};

// ---- mahjong, golden flower ---------------------------------------------------------------------------

/** Mahjong tile: ivory face, jade back edge, 1 / 2 / 3 / 5 dots (data) in col / col2 — no glyphs. */
export const tile: Draw = (g, p) => {
  g.fillStyle = '#2F8F6A';
  g.beginPath();
  g.roundRect?.(-0.66, -0.86, 1.36, 1.86, 0.18);
  if (!g.roundRect) g.rect(-0.66, -0.86, 1.36, 1.86);
  g.fill();
  g.fillStyle = '#F6F1E4';
  g.beginPath();
  g.roundRect?.(-0.7, -0.94, 1.36, 1.86, 0.18);
  if (!g.roundRect) g.rect(-0.7, -0.94, 1.36, 1.86);
  g.fill();
  const n = [1, 2, 3, 5][p.data % 4] ?? 1;
  const at: Record<number, [number, number][]> = {
    1: [[-0.02, -0.01]],
    2: [
      [-0.02, -0.45],
      [-0.02, 0.43],
    ],
    3: [
      [-0.32, -0.52],
      [-0.02, -0.01],
      [0.28, 0.5],
    ],
    5: [
      [-0.32, -0.5],
      [0.28, -0.5],
      [-0.02, -0.01],
      [-0.32, 0.48],
      [0.28, 0.48],
    ],
  };
  const r = n === 1 ? 0.4 : n === 2 ? 0.27 : 0.2;
  for (const [i, [x, y]] of (at[n] ?? []).entries()) {
    g.fillStyle = i % 2 ? p.col2 : p.col;
    circle(g, x, y, r);
    g.fillStyle = '#F6F1E4';
    circle(g, x, y, r * 0.42);
  }
};

/** Plum blossom (梅花): five round petals, stamens. */
export const plum: Draw = (g, p) => {
  g.fillStyle = p.col;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * TAU - Math.PI / 2;
    circle(g, Math.cos(a) * 0.52, Math.sin(a) * 0.52, 0.46);
  }
  g.fillStyle = p.col2;
  circle(g, 0, 0, 0.28);
  g.fillStyle = '#FFF6E0';
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * TAU - Math.PI / 2 + 0.6;
    circle(g, Math.cos(a) * 0.3, Math.sin(a) * 0.3, 0.07);
  }
};

// ---- rounds: pot wheel, rockets -----------------------------------------------------------------------

/** A wheel segment (wedge) of the Pot wheel. */
export const wedge: Draw = (g, p) => {
  const a = 0.42;
  g.fillStyle = p.col;
  g.beginPath();
  g.arc(-0.7, 0, 1.6, -a, a);
  g.arc(-0.7, 0, 0.55, a, -a, true);
  g.closePath();
  g.fill();
  g.strokeStyle = p.col2;
  g.lineWidth = 0.08;
  g.stroke();
};

/** A small rocket heading along +x (face: true). */
export const rocket: Draw = (g, p) => {
  g.fillStyle = p.col2;
  fill(g, 'M-.5-.22L-.9-.5L-.7 0L-.9 .5L-.5 .22Z');
  g.fillStyle = p.col;
  fill(g, 'M1 0C.7-.3 .2-.3-.6-.24L-.6 .24C.2 .3 .7 .3 1 0Z');
  g.fillStyle = '#FFFFFF';
  circle(g, 0.35, 0, 0.1);
};

export type ShapeName = keyof typeof SHAPES;
export const SHAPES = {
  glow,
  dot,
  paper,
  spark,
  twinkle,
  star,
  pixel,
  diamond,
  puff,
  petal,
  leaf,
  butterfly,
  bubble,
  fish,
  planet,
  ring,
  sprinkle,
  lollipop,
  candy,
  shard,
  gear,
  nut,
  drop,
  lotus,
  koi,
  gull,
  firecracker,
  redPaper,
  envelope,
  lantern,
  ingot,
  tile,
  plum,
  wedge,
  rocket,
} satisfies Record<string, Draw>;

/** For actors: draw a shape at (x, y) with a size and rotation in CSS px (the context is at dpr scale). */
export function stamp(
  g: CanvasRenderingContext2D,
  draw: Draw,
  x: number,
  y: number,
  size: number,
  rot: number,
  col: string,
  col2: string,
  dpr: number,
  extra: Partial<Particle> = {},
): void {
  const co = Math.cos(rot) * size * dpr;
  const si = Math.sin(rot) * size * dpr;
  const alpha = g.globalAlpha;
  g.setTransform(co, si, -si, co, x * dpr, y * dpr);
  draw(g, { col, col2, t: 0, ph: 0, vx: 0, vy: 0, size, data: 0, ...extra } as Particle);
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  // shapes may dim the alpha for their highlights
  g.globalAlpha = alpha;
}
